import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Badge, Button, Card, Alert, Form, Spinner } from 'react-bootstrap';
import DatePeriodNavigation, { PeriodChangeEvent } from './common/DatePeriodNavigation';
import { DataTable } from './common/DataTable';
import type { ColumnConfig } from './common/DataTable/types';
import { FilterBar } from './common/FilterControls';
import type { FilterDefinition } from './common/FilterControls';
import { ApiErrorBanner } from './common/ApiErrorBanner';
import { getInvoicePayments } from '../api/generated/arrayscapeGamingDashboardAPI';
import type {
  Payment,
  PaymentMethod,
  GetInvoicePaymentsParams,
  GetInvoicePayments200,
} from '../api/generated/model';
import { CopyableText } from './common/Copyable';
import MatchBlockersCell from './common/MatchBlockers';
import { usePaginatedQuery } from '../hooks/usePaginatedQuery';
import { qboPaymentUrl } from './InvoiceDetailContent';

const FETCH_LIMIT = 50;

const formatCurrency = (amountStr?: string): string => {
  if (!amountStr) return '';
  const n = parseFloat(amountStr);
  if (Number.isNaN(n)) return amountStr;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);
};

const formatDate = (iso?: string): string => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

const sourceBadgeVariant = (srcType?: string): string => {
  switch (srcType) {
    case 'quickbooks': return 'info';
    default: return 'secondary';
  }
};

const sourceLabel = (srcType?: string): string => {
  switch (srcType) {
    case 'quickbooks': return 'QB';
    default: return srcType || '';
  }
};

const methodLabel = (m?: PaymentMethod): string => {
  switch (m) {
    case 'cash': return 'Cash';
    case 'check': return 'Check';
    case 'ach': return 'ACH';
    case 'credit_card': return 'Credit Card';
    case 'debit_card': return 'Debit Card';
    case 'wire': return 'Wire';
    case 'other': return 'Other';
    default: return '';
  }
};

const linkStatusBadge = (s?: string) => {
  switch (s) {
    case 'linked': return <Badge bg="success">Linked</Badge>;
    case 'unlinked': return <Badge bg="warning" text="dark">Unlinked</Badge>;
    case 'unlinkable': return <Badge bg="secondary">Unlinkable</Badge>;
    case 'stranded': return <Badge bg="danger">Stranded</Badge>;
    default: return null;
  }
};

export const paymentFilterKeys = [
  'id', 'src_type', 'customer_id', 'customer_name', 'reference', 'note', 'link_status', 'method', 'invoice_number',
] as const;

const TriStateCheckbox: React.FC<{ state: 'none' | 'some' | 'all'; ariaLabel?: string }> = ({ state, ariaLabel }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = state === 'some';
  }, [state]);
  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input"
      checked={state === 'all'}
      readOnly
      tabIndex={-1}
      style={{ pointerEvents: 'none', margin: 0 }}
      aria-label={ariaLabel}
    />
  );
};

export interface PaymentsTableProps {
  initialDateRange?: { start_date: string; end_date: string };
  initialPeriod?: string;
  initialFilters?: Record<string, any>;
  initialSort?: { field: string; direction: 'asc' | 'desc' };

  onDateRangeChange?: (range: { start_date: string; end_date: string }, period: string) => void;
  onFiltersChange?: (filters: Record<string, any>) => void;
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;

  // Candidate filter (modal use): when provided, an extra "Candidates only"
  // toggle is added to the filter bar, initially checked. When checked the
  // table renders the pre-fetched candidate Payments in-memory (no API hit)
  // so the user sees the suggested matches immediately.
  candidatePayments?: Payment[];

  // Selection (modal use): when both selectedIds and onSelectionChange are
  // passed, a leading checkbox column is rendered. IDs are the composite
  // `<src_id>:<id>` form so the same string maps cleanly to the BTP POST
  // payload server-side.
  selectedIds?: string[];
  onSelectionChange?: (ids: string[]) => void;

  onRowClick?: (p: Payment) => void;
  onOpenInvoice?: (compositeInvoiceId: string) => void;
  onOpenCustomer?: (customerId: string) => void;

  // Per-row "Revalidate" handler — when set, a small refresh button is
  // shown in the Match Issues column. The button calls this; the parent
  // is responsible for hitting the refetch endpoint and invalidating the
  // candidates query so blockers refresh in place.
  onRevalidate?: (p: Payment) => Promise<void>;

  title?: string;
  titleIcon?: string;
  showDateNav?: boolean;
  countLabel?: (n?: number) => string;

  preferencesKey: string;
  userId?: string;
  userPreferences?: any;

  refreshKey?: number;
}

// Payment.id from the API is already in composite `<src_id>:<id>` form
// (Payment.CompositeID() server-side), so use it verbatim — re-prepending
// src_id would double up.
const compositePaymentId = (p: Payment): string => p.id || '';

const PaymentsTable: React.FC<PaymentsTableProps> = ({
  initialDateRange,
  initialPeriod,
  initialFilters,
  initialSort,
  onDateRangeChange,
  onFiltersChange,
  onSortChange,
  candidatePayments,
  selectedIds,
  onSelectionChange,
  onRowClick,
  onOpenInvoice,
  onOpenCustomer,
  onRevalidate,
  title = 'Invoice Payments for',
  titleIcon = 'bi-cash-coin',
  showDateNav = true,
  countLabel,
  preferencesKey,
  userId,
  userPreferences,
  refreshKey,
}) => {
  const defaultDateRange = useMemo(() => {
    if (initialDateRange) return initialDateRange;
    const now = new Date();
    return {
      start_date: `${now.getFullYear()}-01-01`,
      end_date: `${now.getFullYear()}-12-31`,
    };
  }, [initialDateRange]);

  const candidatesAvailable = !!candidatePayments && candidatePayments.length > 0;

  const [period, setPeriod] = useState<string>(initialPeriod || 'year');
  const [dateRange, setDateRange] = useState<{ start_date: string; end_date: string }>(defaultDateRange);
  const [filters, setFilters] = useState<Record<string, any>>(() => ({ ...(initialFilters || {}) }));
  const autoSelectedCandidatesRef = useRef(false);
  useEffect(() => {
    if (candidatesAvailable && !autoSelectedCandidatesRef.current) {
      setFilters(prev => prev.candidates_only === undefined ? { ...prev, candidates_only: 'on' } : prev);
      autoSelectedCandidatesRef.current = true;
    }
  }, [candidatesAvailable]);
  const [showFilterBar, setShowFilterBar] = useState<boolean>(() => {
    return Object.entries(filters).some(([_, v]) => v);
  });
  const [sortField, setSortField] = useState(initialSort?.field || 'txn_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>(initialSort?.direction || 'desc');

  // Composite-id set of rows whose Revalidate button is currently in
  // flight. Read inside the renderCell via ref so the column closures
  // (captured once at table mount) see the live value.
  const [revalidatingIds, setRevalidatingIds] = useState<Set<string>>(new Set());
  const revalidatingIdsRef = useRef(revalidatingIds);
  revalidatingIdsRef.current = revalidatingIds;
  const onRevalidateRef = useRef(onRevalidate);
  onRevalidateRef.current = onRevalidate;
  const handleRevalidateRow = useCallback(async (p: Payment) => {
    const id = p.id || '';
    if (!id || !onRevalidateRef.current) return;
    setRevalidatingIds(prev => {
      const n = new Set(prev);
      n.add(id);
      return n;
    });
    try {
      await onRevalidateRef.current(p);
    } finally {
      setRevalidatingIds(prev => {
        const n = new Set(prev);
        n.delete(id);
        return n;
      });
    }
  }, []);

  const candidatesOnly = candidatesAvailable && filters.candidates_only === 'on';

  useEffect(() => { onDateRangeChange?.(dateRange, period); }, [dateRange, period, onDateRangeChange]);
  useEffect(() => { onFiltersChange?.(filters); }, [filters, onFiltersChange]);
  useEffect(() => { onSortChange?.(sortField, sortDir); }, [sortField, sortDir, onSortChange]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(v => v).length,
    [filters]
  );

  const handleFilterChange = useCallback((field: string, value: string | undefined) => {
    setFilters(prev => ({ ...prev, [field]: value || undefined }));
  }, []);
  const handleClearFilters = useCallback(() => setFilters({}), []);

  const buildQueryParams = useCallback(
    (cursor: string | undefined) => {
      if (cursor) return { cursor, limit: FETCH_LIMIT };
      const isAll = period === 'all';
      return {
        ...(!isAll && dateRange.start_date ? { start_date: dateRange.start_date } : {}),
        ...(!isAll && dateRange.end_date ? { end_date: dateRange.end_date } : {}),
        sort_field: sortField,
        sort_direction: sortDir,
        limit: FETCH_LIMIT,
        ...(filters.id ? { id: filters.id } : {}),
        ...(filters.src_type ? { src_type: filters.src_type } : {}),
        ...(filters.customer_id ? { customer_id: filters.customer_id } : {}),
        ...(filters.customer_name ? { customer_name: filters.customer_name } : {}),
        ...(filters.reference ? { reference: filters.reference } : {}),
        ...(filters.note ? { note: filters.note } : {}),
        ...(filters.link_status ? { link_status: filters.link_status } : {}),
        ...(filters.method ? { method: filters.method } : {}),
        ...(filters.invoice_number ? { invoice_number: filters.invoice_number } : {}),
      };
    },
    [dateRange, filters, period, sortField, sortDir]
  );

  const {
    allItems: apiItems,
    totalCount: apiTotal,
    isLoading,
    hasMore,
    handleLoadMore,
    reset,
    error: paginationError,
  } = usePaginatedQuery<Payment, GetInvoicePayments200, unknown>({
    queryKey: `invoice-payments:${preferencesKey}`,
    buildQueryParams,
    queryFn: (params) => getInvoicePayments(params as GetInvoicePaymentsParams),
    getRowKey: compositePaymentId,
    enabled: !candidatesOnly,
  });

  // Refresh when parent bumps refreshKey. Use reset() (which also wipes
  // the react-query cache for this hook's key) instead of setAllItems([]),
  // so a mutation that produces a byte-identical list response — e.g. a
  // per-row Revalidate that didn't change anything visible to the list
  // query — still triggers the accumulator effect to repopulate. See the
  // reset() doc comment in usePaginatedQuery.
  useEffect(() => {
    if (refreshKey === undefined) return;
    reset();
  }, [refreshKey, reset]);

  const handlePeriodChange = (event: PeriodChangeEvent) => {
    setPeriod(event.navigationMode);
    setDateRange({
      start_date: event.dateRange.start_date.split('T')[0],
      end_date: event.dateRange.end_date.split('T')[0],
    });
  };

  // Selection — same ref-based pattern as BankTransactionsTable to dodge the
  // DataTable "frozen columns" trap.
  const selectionEnabled = !!selectedIds && !!onSelectionChange;
  const selectedSet = useMemo(() => new Set(selectedIds || []), [selectedIds]);
  const selectedSetRef = useRef(selectedSet);
  selectedSetRef.current = selectedSet;
  const displayedItemsRef = useRef<Payment[]>([]);
  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;

  const displayedItems: Payment[] = candidatesOnly && candidatePayments
    ? candidatePayments
    : apiItems;
  displayedItemsRef.current = displayedItems;
  const displayedTotal = candidatesOnly && candidatePayments
    ? candidatePayments.length
    : apiTotal;
  const displayedHasMore = candidatesOnly ? false : hasMore;

  const toggleRow = useCallback((id: string) => {
    const cb = onSelectionChangeRef.current;
    if (!cb) return;
    const next = new Set(selectedSetRef.current);
    if (next.has(id)) next.delete(id); else next.add(id);
    cb(Array.from(next));
  }, []);

  const toggleAllOnPage = useCallback(() => {
    const cb = onSelectionChangeRef.current;
    if (!cb) return;
    const next = new Set(selectedSetRef.current);
    const ids = displayedItemsRef.current.map(compositePaymentId).filter(Boolean);
    const allSelected = ids.length > 0 && ids.every(id => next.has(id));
    if (allSelected) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    cb(Array.from(next));
  }, []);

  const filterDefs = useMemo((): FilterDefinition[] => {
    const defs: FilterDefinition[] = [];
    if (candidatesAvailable) {
      defs.push({
        type: 'select', field: 'candidates_only', label: 'Show', icon: 'bi-magic',
        options: [{ value: 'on', label: 'Candidates' }],
      });
    }
    defs.push(
      { type: 'text', field: 'id', label: 'ID', icon: 'bi-hash', placeholder: 'Substring match...' },
      {
        type: 'select', field: 'src_type', label: 'Source', icon: 'bi-tag',
        options: [{ value: 'quickbooks', label: 'QuickBooks' }],
      },
      {
        type: 'multiselect', field: 'link_status', label: 'Link Status', icon: 'bi-link-45deg',
        options: [
          { value: 'linked', label: 'Linked' },
          { value: 'unlinked', label: 'Unlinked' },
          { value: 'unlinkable', label: 'Unlinkable' },
          { value: 'stranded', label: 'Stranded' },
        ],
      },
      {
        type: 'multiselect', field: 'method', label: 'Method', icon: 'bi-credit-card',
        options: [
          { value: 'cash', label: 'Cash' },
          { value: 'check', label: 'Check' },
          { value: 'ach', label: 'ACH' },
          { value: 'credit_card', label: 'Credit Card' },
          { value: 'debit_card', label: 'Debit Card' },
          { value: 'wire', label: 'Wire' },
          { value: 'other', label: 'Other' },
        ],
      },
      { type: 'text', field: 'customer_name', label: 'Customer', icon: 'bi-person', placeholder: 'Substring match...' },
      { type: 'text', field: 'invoice_number', label: 'Invoice #', icon: 'bi-file-earmark-text', placeholder: 'e.g. 1720 or 1720-N' },
      { type: 'text', field: 'reference', label: 'Reference', icon: 'bi-hash', placeholder: 'Substring match...' },
      { type: 'text', field: 'note', label: 'Note', icon: 'bi-chat-left-text', placeholder: 'Substring match...' },
    );
    return defs;
  }, [candidatesAvailable]);

  const columns: ColumnConfig<Payment>[] = useMemo(() => {
    const cols: ColumnConfig<Payment>[] = [];
    if (selectionEnabled) {
      cols.push({
        key: '__select',
        label: '',
        width: 40,
        minWidth: 40,
        renderHeader: () => {
          const set = selectedSetRef.current;
          const ids = displayedItemsRef.current.map(compositePaymentId).filter(Boolean);
          let state: 'none' | 'some' | 'all' = 'none';
          if (set.size > 0) {
            const allSelected = ids.length > 0 && ids.every(id => set.has(id));
            state = allSelected ? 'all' : 'some';
          }
          return <TriStateCheckbox state={state} ariaLabel="Select all on page" />;
        },
        headerOnClick: () => toggleAllOnPage(),
        onCellClick: (p) => {
          const id = compositePaymentId(p);
          if (id) toggleRow(id);
        },
        renderCell: (p) => {
          const id = compositePaymentId(p);
          return (
            <div className="d-flex justify-content-center" style={{ pointerEvents: 'none' }}>
              <Form.Check type="checkbox" checked={!!id && selectedSetRef.current.has(id)} readOnly tabIndex={-1} />
            </div>
          );
        },
      });
    }
    cols.push(
      {
        key: 'id',
        label: 'ID',
        width: 220,
        minWidth: 100,
        renderCell: (p) => {
          if (!p.id) return null;
          const qbUrl = qboPaymentUrl(p);
          if (qbUrl) {
            return (
              <a
                href={qbUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="p-0 text-decoration-none font-monospace small text-truncate d-block"
                title={`${p.id} (open in QuickBooks)`}
              >
                {p.id}
              </a>
            );
          }
          return (
            <CopyableText
              value={p.id}
              className="font-monospace small d-block text-truncate"
              title={`${p.id} (click to copy)`}
            />
          );
        },
      },
      {
        key: 'src_type', label: 'Source', width: 70,
        renderCell: (p) => <Badge bg={sourceBadgeVariant(p.src_type)}>{sourceLabel(p.src_type)}</Badge>,
      },
      {
        key: 'txn_date', label: 'Date', width: 110, sortable: true,
        renderCell: (p) => <>{formatDate(p.txn_date)}</>,
      },
      {
        key: 'customer_name', label: 'Customer', width: 200, minWidth: 120, sortable: true,
        renderCell: (p) => {
          if (!p.customer_id) return <span className="text-muted">—</span>;
          const label = p.customer_name || p.customer_id;
          if (!onOpenCustomer) {
            return <span className="text-truncate d-block" title={p.customer_name || p.customer_id}>{label}</span>;
          }
          return (
            <Button
              variant="link"
              className="p-0 text-decoration-none text-truncate text-start d-block"
              style={{ minWidth: 0, maxWidth: '100%' }}
              onClick={(e) => { e.stopPropagation(); p.customer_id && onOpenCustomer(p.customer_id); }}
              title={p.customer_name ? p.customer_name : `${p.customer_id} (no name resolved)`}
            >
              {label}
            </Button>
          );
        },
      },
      {
        key: 'total_amount', label: 'Amount', width: 110, sortable: true,
        renderCell: (p) => <span className="d-block text-end">{formatCurrency(p.total_amount)}</span>,
      },
      {
        key: 'method', label: 'Method', width: 110, sortable: true,
        renderCell: (p) => methodLabel(p.method) || <span className="text-muted">—</span>,
      },
      {
        key: 'reference', label: 'Reference', width: 160, minWidth: 100, sortable: true,
        renderCell: (p) => p.reference ? (
          <CopyableText value={p.reference} className="d-block text-truncate" title={`${p.reference} (click to copy)`} />
        ) : <span className="text-muted">—</span>,
      },
      {
        key: 'invoice', label: 'Invoice', width: 200, minWidth: 120, sortable: true,
        renderCell: (p) => {
          const apps = p.applied_invoices || [];
          if (apps.length === 0) return <span className="text-muted">—</span>;
          return (
            <span>
              {apps.map((a, i) => {
                const compositeId = a.invoice_src_id && a.invoice_id
                  ? `${a.invoice_src_id}-${a.invoice_id}`
                  : null;
                const label = a.invoice_number || a.invoice_id || '';
                return (
                  <React.Fragment key={`${a.invoice_src_id}:${a.invoice_id}:${i}`}>
                    {i > 0 && <span className="text-muted">, </span>}
                    {onOpenInvoice && compositeId ? (
                      <Button
                        variant="link"
                        className="p-0 text-decoration-none align-baseline"
                        onClick={(e) => { e.stopPropagation(); onOpenInvoice(compositeId); }}
                        title={a.amount ? `${label} — ${formatCurrency(a.amount)}` : label}
                      >
                        {label}
                      </Button>
                    ) : (
                      <span title={a.amount ? `${label} — ${formatCurrency(a.amount)}` : label}>{label}</span>
                    )}
                  </React.Fragment>
                );
              })}
            </span>
          );
        },
      },
      {
        key: 'link_status', label: 'Link', width: 110,
        renderCell: (p) => linkStatusBadge((p as any).link_status),
      },
      {
        key: 'note', label: 'Note', width: 200, minWidth: 100, sortable: true,
        renderCell: (p) => {
          const text = p.note || '';
          if (!text) return null;
          const short = text.length > 80 ? text.substring(0, 80) + '...' : text;
          return <span className="text-muted" title={text}>{short}</span>;
        },
      },
      {
        key: 'match_blockers', label: 'Match Issues', width: 280, minWidth: 200,
        // Read live revalidating state and onRevalidate via ref — DataTable
        // captures column defs once via useState (per feedback_datatable_renderCell_state),
        // so a closure over those values directly would stay stale.
        renderCell: (p) => {
          const id = p.id || '';
          const isRevalidating = revalidatingIdsRef.current.has(id);
          const showButton = !!onRevalidateRef.current && !!id;
          return (
            <div className="d-flex align-items-center gap-1 flex-wrap">
              <MatchBlockersCell blockers={p.match_blockers} />
              {showButton && (
                <Button
                  size="sm"
                  variant={isRevalidating ? 'primary' : 'outline-secondary'}
                  className="d-inline-flex align-items-center gap-1 py-0 px-2"
                  disabled={isRevalidating}
                  onClick={(e) => {
                    e.stopPropagation();
                    void handleRevalidateRow(p);
                  }}
                  title="Refetch this Payment from QuickBooks and re-run the auto-linker"
                >
                  {isRevalidating
                    ? <><Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /><span>Revalidating…</span></>
                    : <><i className="bi bi-arrow-clockwise" /><span>Revalidate</span></>}
                </Button>
              )}
            </div>
          );
        },
      },
    );
    return cols;
  }, [selectionEnabled, selectedSet, displayedItems, toggleRow, toggleAllOnPage, onOpenInvoice, onOpenCustomer]);

  const handleRowClick = useCallback((p: Payment) => {
    if (onRowClick) onRowClick(p);
  }, [onRowClick]);
  // Selection mode reserves row-click for opening detail (when an
  // onRowClick is supplied). Selection toggles only via the __select
  // column's checkbox. Mirror of BankTransactionsTable's behavior.
  const rowClickProp = selectionEnabled
    ? (onRowClick ? handleRowClick : undefined)
    : handleRowClick;

  return (
    <Card className="d-flex flex-column flex-grow-1" style={{ minHeight: 0, overflow: 'hidden' }}>
      <Card.Header className="bg-primary text-white flex-shrink-0">
        <div className="d-flex justify-content-between align-items-center m-1 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="bg-header-control rounded px-2 py-1 d-flex align-items-center gap-2">
              {title && (
                <span className="text-primary fw-semibold">
                  <i className={`bi ${titleIcon} me-1`}></i>
                  {title}
                </span>
              )}
              {showDateNav && (
                <DatePeriodNavigation
                  onPeriodChange={handlePeriodChange}
                  dateFormat="date-only"
                  initialNavigationMode={period as any}
                  initialDateRange={{ start_date: dateRange.start_date, end_date: dateRange.end_date }}
                />
              )}
              {showDateNav && <div className="border-end border-primary mx-2" style={{ height: '24px' }}></div>}
              <Button
                variant={activeFilterCount > 0 ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setShowFilterBar(!showFilterBar)}
              >
                <i className="bi bi-funnel me-1"></i>
                Filters
                {activeFilterCount > 0 && (
                  <span className="badge bg-header-control text-primary ms-1">{activeFilterCount}</span>
                )}
              </Button>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="bg-header-control rounded px-3 py-1">
              <span className="text-primary">
                {countLabel
                  ? countLabel(displayedTotal)
                  : (displayedTotal !== undefined ? `${displayedTotal} payments` : `${displayedItems.length} payments`)}
              </span>
            </div>
          </div>
        </div>
        {showFilterBar && (
          <FilterBar
            filters={filters}
            filterDefs={filterDefs}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        )}
      </Card.Header>
      <ApiErrorBanner error={paginationError} />
      <Card.Body className="flex-grow-1 overflow-hidden p-0 d-flex flex-column" style={{ minHeight: 0 }}>
        <DataTable
          data={displayedItems}
          columns={columns}
          getRowKey={compositePaymentId}
          sortConfig={{ field: sortField, direction: sortDir }}
          onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
          onRowClick={rowClickProp}
          pagination={{ hasMore: displayedHasMore, limit: FETCH_LIMIT }}
          onLoadMore={handleLoadMore}
          isLoading={isLoading}
          preferencesKey={preferencesKey}
          userId={userId}
          userPreferences={userPreferences}
          maxHeight="100%"
          enableStickyHeader={true}
          enableColumnReorder={true}
          enableColumnResize={true}
          enableColumnVisibility={true}
          emptyState={<Alert variant="secondary" className="m-3">No payments found.</Alert>}
        />
      </Card.Body>
    </Card>
  );
};

export default PaymentsTable;
